import { RootState } from '@app/store';
import { useTypedSelector } from '@app/store/hooks/useTypedSelector';
import { PayloadAction, createSelector, createSlice, current, isDraft } from '@reduxjs/toolkit';

import {
  cloneEditorPolicy,
  createEditorPolicy,
  createEditorRule,
  diffPolicies,
  toEditorPolicies,
} from '../lib/editorMappers';
import { readEditorSession, removeEditorSession, writeEditorSession } from '../lib/editorStorage';
import { validateDraft } from '../lib/validateDraft';
import {
  EDITOR_SESSION_SCHEMA_VERSION,
  IEditorChanges,
  IEditorPolicyInput,
  IEditorRuleInput,
  IEditorSession,
  IRoutingConfigVersion,
} from '../model/types';

// The on-screen version's edit session, mirrored to localStorage per version id.
interface IRoutingConfigEditorState {
  session: IEditorSession | null;
  // Set when hydration dropped a stale session; the widget toasts once and acknowledges.
  staleDroppedVersionId: number | null;
}

interface IHydratePayload {
  versionId: number;
  updatedAt: string;
}

interface IUpdatePolicyPayload {
  uid: string;
  input: IEditorPolicyInput;
}

interface IMovePolicyPayload {
  uid: string;
  // Clamped to the list bounds.
  toIndex: number;
}

interface IRuleRef {
  policyUid: string;
  ruleUid: string;
}

interface IMoveRulePayload extends IRuleRef {
  toIndex: number;
}

interface IAddRulePayload {
  policyUid: string;
  input: IEditorRuleInput;
}

interface IUpdateRulePayload extends IRuleRef {
  input: IEditorRuleInput;
}

// Returns false when nothing moved.
const moveItem = <T extends { uid: string }>(list: T[], uid: string, toIndex: number): boolean => {
  const from = list.findIndex(item => item.uid === uid);
  if (from === -1) return false;
  const to = Math.max(0, Math.min(toIndex, list.length - 1));
  if (to === from) return false;
  const [item] = list.splice(from, 1);
  list.splice(to, 0, item);
  return true;
};

const findPolicy = (state: IRoutingConfigEditorState, uid: string) =>
  state.session?.draft.find(item => item.uid === uid);

const findRule = (state: IRoutingConfigEditorState, { policyUid, ruleUid }: IRuleRef) =>
  findPolicy(state, policyUid)?.rules.find(item => item.uid === ruleUid);

const initialState: IRoutingConfigEditorState = {
  session: null,
  staleDroppedVersionId: null,
};

// localStorage-in-reducer, like the geo slice; `current` unwraps the immer draft.
const persist = (session: IEditorSession) =>
  writeEditorSession(isDraft(session) ? current(session) : session);

const endSession = (state: IRoutingConfigEditorState) => {
  if (state.session) removeEditorSession(state.session.versionId);
  state.session = null;
};

const routingConfigEditorSlice = createSlice({
  name: 'routingConfigEditor',
  initialState,
  reducers: {
    // On each version (re)load: restore its session, drop it if the server copy
    // moved on, leave other versions' sessions dormant.
    hydrateEditor: (state, { payload: { versionId, updatedAt } }: PayloadAction<IHydratePayload>) => {
      const live = state.session;

      if (live && live.versionId === versionId) {
        if (live.baseUpdatedAt === updatedAt) return;
        endSession(state);
        state.staleDroppedVersionId = versionId;
        return;
      }

      // A clean session on the version we're leaving isn't worth keeping.
      if (live && !diffPolicies(live.original, live.draft).isDirty) {
        removeEditorSession(live.versionId);
      }

      const stored = readEditorSession(versionId);
      if (!stored) {
        state.session = null;
        return;
      }
      if (stored.baseUpdatedAt !== updatedAt) {
        removeEditorSession(versionId);
        state.session = null;
        state.staleDroppedVersionId = versionId;
        return;
      }
      state.session = stored;
    },
    startEditing: (state, { payload: version }: PayloadAction<IRoutingConfigVersion>) => {
      const policies = toEditorPolicies(version.policies);
      state.session = {
        schemaVersion: EDITOR_SESSION_SCHEMA_VERSION,
        versionId: version.id,
        baseStatus: version.status,
        baseUpdatedAt: version.updated_at,
        original: policies,
        draft: toEditorPolicies(version.policies),
        // Editing a draft writes straight into it; other bases get a draft on Save Draft.
        savedDraftId: version.status === 'draft' ? version.id : null,
      };
      persist(state.session);
    },
    togglePolicyActive: (state, { payload: uid }: PayloadAction<string>) => {
      const policy = state.session?.draft.find(item => item.uid === uid);
      if (!policy) return;
      policy.active = !policy.active;
      persist(state.session);
    },
    // Appended last (lowest priority) so it never pre-empts existing routing.
    addPolicy: (state, { payload: input }: PayloadAction<IEditorPolicyInput>) => {
      if (!state.session) return;
      state.session.draft.push(createEditorPolicy(input));
      persist(state.session);
    },
    updatePolicy: (state, { payload: { uid, input } }: PayloadAction<IUpdatePolicyPayload>) => {
      const policy = state.session?.draft.find(item => item.uid === uid);
      if (!policy) return;
      policy.name = input.name;
      policy.description = input.description;
      policy.conditions = input.conditions;
      persist(state.session);
    },
    clonePolicy: (state, { payload: uid }: PayloadAction<string>) => {
      const draft = state.session?.draft;
      const index = draft?.findIndex(item => item.uid === uid) ?? -1;
      if (!draft || index === -1) return;
      // `current` unwraps the immer proxy so the copy is a plain object.
      draft.splice(index + 1, 0, cloneEditorPolicy(current(draft[index])));
      persist(state.session);
    },
    removePolicy: (state, { payload: uid }: PayloadAction<string>) => {
      const draft = state.session?.draft;
      const index = draft?.findIndex(item => item.uid === uid) ?? -1;
      if (!draft || index === -1) return;
      draft.splice(index, 1);
      persist(state.session);
    },
    movePolicy: (state, { payload: { uid, toIndex } }: PayloadAction<IMovePolicyPayload>) => {
      if (!state.session || !moveItem(state.session.draft, uid, toIndex)) return;
      persist(state.session);
    },
    moveRule: (state, { payload: { policyUid, ruleUid, toIndex } }: PayloadAction<IMoveRulePayload>) => {
      const policy = findPolicy(state, policyUid);
      if (!policy || !moveItem(policy.rules, ruleUid, toIndex)) return;
      persist(state.session);
    },
    // Appended last within its policy (lowest priority there).
    addRule: (state, { payload: { policyUid, input } }: PayloadAction<IAddRulePayload>) => {
      const policy = findPolicy(state, policyUid);
      if (!policy) return;
      policy.rules.push(createEditorRule(input));
      persist(state.session);
    },
    updateRule: (state, { payload: { input, ...ref } }: PayloadAction<IUpdateRulePayload>) => {
      const rule = findRule(state, ref);
      if (!rule) return;
      rule.name = input.name;
      rule.description = input.description;
      rule.min = input.min;
      rule.max = input.max;
      rule.conditions = input.conditions;
      rule.selections = input.selections;
      rule.sort_strategy = input.sort_strategy;
      persist(state.session);
    },
    removeRule: (state, { payload: { policyUid, ruleUid } }: PayloadAction<IRuleRef>) => {
      const rules = findPolicy(state, policyUid)?.rules;
      const index = rules?.findIndex(item => item.uid === ruleUid) ?? -1;
      if (!rules || index === -1) return;
      rules.splice(index, 1);
      persist(state.session);
    },
    // A config edit like the policy toggle — nothing is live until Publish.
    toggleRuleActive: (state, { payload }: PayloadAction<IRuleRef>) => {
      const rule = findRule(state, payload);
      if (!rule) return;
      rule.active = !rule.active;
      persist(state.session);
    },
    // A draft was created from a published/archived base: move the session onto it.
    moveSessionToDraft: (state, { payload: draft }: PayloadAction<IRoutingConfigVersion>) => {
      if (!state.session) return;
      removeEditorSession(state.session.versionId);
      state.session.versionId = draft.id;
      state.session.baseStatus = draft.status;
      state.session.baseUpdatedAt = draft.updated_at;
      state.session.savedDraftId = draft.id;
      persist(state.session);
    },
    // A metadata PATCH bumps updated_at on the server. Refresh the guard, or the next
    // GET of this version reads as an edit made elsewhere and drops the live session.
    syncSessionBase: (state, { payload: version }: PayloadAction<IRoutingConfigVersion>) => {
      if (!state.session || state.session.versionId !== version.id) return;
      state.session.baseUpdatedAt = version.updated_at;
      persist(state.session);
    },
    discardEditing: endSession,
    finishEditing: endSession,
    acknowledgeStaleDrop: state => {
      state.staleDroppedVersionId = null;
    },
  },
});

export const actions = routingConfigEditorSlice.actions;
export default routingConfigEditorSlice.reducer;

const NO_CHANGES: IEditorChanges = {
  count: 0,
  changedUids: [],
  addedUids: [],
  changedRuleUids: [],
  addedRuleUids: [],
  isDirty: false,
  summary: [],
  issues: [],
};

export const selectEditorSession = (state: RootState) => state.routingConfigEditor.session;

export const selectEditorChanges = createSelector(
  [selectEditorSession],
  (session): IEditorChanges =>
    session
      ? { ...diffPolicies(session.original, session.draft), issues: validateDraft(session.draft) }
      : NO_CHANGES,
);

export const useEditorSession = () => useTypedSelector(selectEditorSession);
export const useEditorChanges = () => useTypedSelector(selectEditorChanges);
export const useStaleDroppedVersionId = () =>
  useTypedSelector((state: RootState) => state.routingConfigEditor.staleDroppedVersionId);
